import {
	TEDCApplicant,
	TQuestionWithAnswer,
	VALIDATION_STATUS,
	FIX_TYPE,
	getValidationResult,
	getFixTypeFromFixableItems,
	ValidationResult,
	ValidationItem,
	useEDCFlow
} from "@smvtech/x-flux";

import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import { useDocumentItem } from "pages/EDCFlow/hooks/useDocumentItem";
import Mixpanel from "utils/mixpanel";
import DocumentPreview from "pages/EDCFlow/components/VisaForm/DocumentsList/DocumentPreview";
import DocumentUpload from "pages/EDCFlow/components/VisaForm/DocumentsList/DocumentUpload";

import { useMemo, useState } from "react";

import BottomDrawer from "../../components/BottomDrawer";
import DocumentVerificationStatus from "pages/EDCFlow/components/VisaForm/DocumentsList/DocumentListItem/DocumentVerificationStatus";
import UploadingProgress from "../../components/UploadingProgress";

import { BugBeetle, Check, Sparkle, UploadSimple, Spinner, XCircle, Info, ProhibitInset } from "@phosphor-icons/react";
import toast from "components/common/Toaster";
import { AxiosError } from "axios";
import { Tooltip } from "antd";

type Props = {
	document: TQuestionWithAnswer;
	activeApplicantData: TEDCApplicant;
	questions: TQuestionWithAnswer[];
	isDocumentsComplete: boolean;
	onDocumentsCompleted: () => void;
	hasFormErrors?: boolean;
	onViewFormErrors?: () => void;
	formErrorItems?: ValidationItem[];
};

type DrawerType = "manual_fixable" | "auto_fixable" | "unfixable" | "form_error" | null;

type DocumentUIState =
	| "not_uploaded"
	| "upload_failed"
	| "perfect"
	| "manual_fixable"
	| "auto_fixable"
	| "unfixable"
	| "form_error";

type UIConfig = {
	badge?: {
		text: string;
		icon: any;
		className: string;
	};
	buttonText: string;
	buttonStyle: string;
	buttonIcon?: any;
	showKnowWhy?: boolean;
	topText?: string;
	bottomText?: string | null;
	showViewErrors?: boolean;
	viewButtonText?: string;
	viewButtonStyle?: string;
};

const DOCUMENT_UI_CONFIG: Record<DocumentUIState, UIConfig> = {
	upload_failed: {
		badge: {
			text: "Upload Failed",
			icon: XCircle,
			className: "text-red-600 bg-red-50"
		},
		buttonText: "Upload Again",
		buttonStyle: "border-indigo-500 text-indigo-600 hover:bg-indigo-50",
		buttonIcon: UploadSimple
	},

	not_uploaded: {
		buttonText: "Upload",
		buttonStyle: "border-indigo-500 text-indigo-600 hover:bg-indigo-50",
		buttonIcon: UploadSimple
	},

	perfect: {
		badge: {
			text: "Verified",
			icon: Check,
			className: "text-green-700 bg-green-100"
		},
		buttonText: "Replace",
		buttonStyle: "border-indigo-500 text-indigo-600 hover:bg-indigo-50"
	},

	manual_fixable: {
		badge: {
			text: "Verified",
			icon: Check,
			className: "text-green-700 bg-green-100"
		},
		buttonText: "Replace",
		buttonStyle: "border-indigo-500 text-indigo-600 hover:bg-indigo-50"
	},

	auto_fixable: {
		badge: {
			text: "Fixable Issues Identified",
			icon: BugBeetle,
			className: "text-yellow-700 bg-yellow-100"
		},
		buttonText: "Auto-Fix",
		topText: "Enhance this image with Auto-Fix.",
		buttonStyle: "text-white bg-gradient-to-b from-[#F79009] to-[#DC6803] shadow-[0_0_0_1.2px_#DC6803]",
		buttonIcon: Sparkle,
		showViewErrors: true,
		viewButtonText: "View Issues",
		viewButtonStyle: "text-amber-600 bg-amber-50"
	},

	unfixable: {
		badge: {
			text: "This document can't be used",
			icon: ProhibitInset,
			className: "text-red-700 bg-red-100"
		},
		buttonText: "Re-Upload",
		buttonStyle: "bg-red-600 text-white border-red-600 hover:bg-red-700",
		buttonIcon: UploadSimple,
		showViewErrors: true,
		viewButtonText: "View Errors",
		viewButtonStyle: "text-red-600 bg-red-100"
	},

	form_error: {
		badge: {
			text: "This document can't be used",
			icon: ProhibitInset,
			className: "text-red-700 bg-red-100"
		},
		buttonText: "Re-Upload",
		buttonStyle: "bg-red-600 text-white border-red-600 hover:bg-red-700",
		buttonIcon: UploadSimple,
		showViewErrors: true,
		viewButtonText: "View Errors",
		viewButtonStyle: "text-red-600 bg-red-100"
	}
};

const DocumentListItem = ({
	document,
	activeApplicantData,
	questions,
	isDocumentsComplete,
	onDocumentsCompleted,
	hasFormErrors = false,
	onViewFormErrors,
	formErrorItems = []
}: Props) => {
	const { applyAutoFix } = useEDCFlow();

	const validationResult: ValidationResult = useMemo(() => {
		return getValidationResult(document);
	}, [document]);

	const status = validationResult?.status ?? null;

	const [isFixing, setIsFixing] = useState(false);

	const fixType = useMemo(() => {
		if (!validationResult) return null;
		if (validationResult?.status !== VALIDATION_STATUS?.FIXABLE) return null;

		return getFixTypeFromFixableItems(validationResult?.fixable?.items);
	}, [validationResult]);

	const {
		signedUrl,
		isLoading,
		uploading,
		uploadFailed,
		hasDocument,
		isPdf,
		reUploadInputId,
		timeSaved,
		handleUploadStart,
		handleUploadProgress,
		handleUploadComplete,
		handleReUpload,
		handlePreviewClick
	} = useDocumentItem({
		document,
		activeApplicantData,
		questions,
		validationResult
	});

	const [drawerType, setDrawerType] = useState<DrawerType>(null);

	const uiState: DocumentUIState = useMemo(() => {
		if (uploadFailed) return "upload_failed";

		if (status === VALIDATION_STATUS.UNFIXABLE) return "unfixable";

		if (!hasDocument) return "not_uploaded";

		if (status === VALIDATION_STATUS.PERFECT || status === VALIDATION_STATUS.FIXABLE) {
			if (hasFormErrors) return "form_error";
		}

		if (status === VALIDATION_STATUS.PERFECT) return "perfect";

		if (status === VALIDATION_STATUS.FIXABLE) {
			if (fixType === FIX_TYPE.AUTO) return "auto_fixable";
			return "manual_fixable";
		}

		return "not_uploaded";
	}, [uploadFailed, hasDocument, status, fixType, hasFormErrors]);

	const config = DOCUMENT_UI_CONFIG[uiState];

	const { order } = useEDCFlow();
	const isDocumentLocked = !!order?.qc_terms_and_conditions;

	return (
		<div
			className={`bg-white rounded-xl min-h-[159px] shadow-sm overflow-hidden border ${uiState === "unfixable" || uiState === "form_error" ? "border-red-200" : "border-[rgba(0,0,0,0.11)]"}`}>
			<div className="p-4">
				<div className="flex gap-4">
					<div className="w-28 h-[125px] shrink-0">
						<div
							className={`w-full h-full relative rounded-xl border border-dashed flex items-center justify-center overflow-hidden ${uiState === "unfixable" || uiState === "form_error" ? "border-red-400 bg-red-50" : "border-indigo-700 bg-[#F8F7FD]"}`}>
							{hasDocument ? (
								<DocumentPreview
									signedUrl={signedUrl!}
									isPdf={isPdf}
									alt={document?.question?.category}
									onPreviewClick={handlePreviewClick}
									isLoading={isLoading}
								/>
							) : uploading.isUploading ? (
								<div className="flex items-center justify-center w-full h-full">
									<Spinner size={24} className="animate-spin text-primary-600" />
								</div>
							) : (
								<DocumentUpload
									document={document}
									activeApplicantData={activeApplicantData}
									onUploadStart={handleUploadStart}
									onUploadProgress={handleUploadProgress}
									onUploadComplete={handleUploadComplete}
									readOnly
								/>
							)}
						</div>
					</div>

					<div className="flex flex-col flex-1 justify-center min-w-0">
						{config?.badge && (
							<span
								className={`flex items-center gap-1 w-fit text-xs font-medium p-1 rounded-full mb-1.5 ${config.badge.className}`}>
								<config.badge.icon size={14} />
								{config?.badge?.text}
							</span>
						)}

						<div className="flex items-center gap-1 mb-4">
							<p className="text-sm font-medium text-gray-900">{document?.question?.category}</p>
							<Info size={14} className="text-gray-400 shrink-0" />
						</div>

						<input
							type="file"
							accept="image/jpeg,image/png,application/pdf"
							hidden
							id={reUploadInputId}
							onChange={(e) => handleReUpload(e.target.files)}
							disabled={isDocumentLocked}
						/>

						{uploading.isUploading ? (
							<UploadingProgress />
						) : (
							<>
								{config.topText && (
									<p className="text-[11px] font-medium text-[#969696] mb-[7px]">{config.topText}</p>
								)}
								<div className="flex items-center gap-2 w-full">
									{uiState === "auto_fixable" ? (
										<button
											disabled={isDocumentLocked}
											onClick={async () => {
												setIsFixing(true);
												const answerId = document.answer_data?._id;
												const applicationId = activeApplicantData.application_id;

												if (!applicationId || !answerId) {
													toast.error(
														"Missing application or answer ID. Cannot apply auto-fix."
													);
													return;
												}

												try {
													await applyAutoFix(applicationId, answerId);
													Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.AUTO_FIX_APPLIED, {
														applicationId,
														documentId: document.id,
														documentType: document.question.doc_type
													});
												} catch (error) {
													toast.error(
														error instanceof AxiosError
															? error.response?.data?.message
															: "Failed to fix document. Please try again."
													);
												} finally {
													setIsFixing(false);
												}
											}}
											className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition w-full ${
												isDocumentLocked
													? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
													: `${config.buttonStyle} cursor-pointer hover:shadow-sm`
											}`}>
											{isFixing ? "Fixing..." : "Auto-Fix"}
											{config.buttonIcon && <config.buttonIcon size={16} />}
										</button>
									) : (
										<div className={config.showKnowWhy ? "flex-1 min-w-0" : "w-full"}>
											<Tooltip
												styles={{ body: { width: isDocumentLocked ? "200px" : "320px" } }}
												title={isDocumentLocked ? "Order has already been placed." : null}>
												<label
													htmlFor={reUploadInputId}
													className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition whitespace-nowrap w-full ${config.buttonStyle} ${isDocumentLocked ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : ""}`}>
													{config.buttonIcon && <config.buttonIcon size={16} />}
													{config.buttonText}
												</label>
											</Tooltip>
										</div>
									)}

									{config.showKnowWhy && (
										<button
											onClick={() => setDrawerType("manual_fixable")}
											className="text-sm text-gray-600 whitespace-nowrap shrink-0">
											Know Why
										</button>
									)}
								</div>

								{config.bottomText && <p className="text-xs text-gray-500 mt-2">{config.bottomText}</p>}
							</>
						)}
					</div>
				</div>
			</div>

			{config.showViewErrors && (
				<button
					onClick={() => {
						if (uiState === "form_error") {
							setDrawerType("form_error");
						} else {
							setDrawerType(uiState === "auto_fixable" ? "auto_fixable" : "unfixable");
						}
					}}
					className={`w-full text-sm font-medium py-1 border-t border-gray-100 ${config.viewButtonStyle}`}>
					{config.viewButtonText}
				</button>
			)}

			<BottomDrawer open={drawerType === "manual_fixable"} onClose={() => setDrawerType(null)}>
				<DocumentVerificationStatus
					validationResult={validationResult}
					document={document}
					timeSaved={timeSaved}
					reUploadInputId={reUploadInputId}
					fixType={FIX_TYPE.MANUAL}
					applicationId={activeApplicantData.application_id}
					isDocumentsComplete={isDocumentsComplete}
					onDocumentsCompleted={onDocumentsCompleted}
					onClose={() => setDrawerType(null)}
				/>
			</BottomDrawer>

			<BottomDrawer open={drawerType === "auto_fixable"} onClose={() => setDrawerType(null)}>
				<DocumentVerificationStatus
					validationResult={validationResult}
					document={document}
					timeSaved={timeSaved}
					reUploadInputId={reUploadInputId}
					fixType={FIX_TYPE.AUTO}
					applicationId={activeApplicantData.application_id}
				/>
			</BottomDrawer>

			<BottomDrawer open={drawerType === "unfixable"} onClose={() => setDrawerType(null)}>
				<DocumentVerificationStatus
					validationResult={validationResult}
					document={document}
					timeSaved={timeSaved}
					reUploadInputId={reUploadInputId}
					applicationId={activeApplicantData.application_id}
				/>
			</BottomDrawer>

			<BottomDrawer open={drawerType === "form_error"} onClose={() => setDrawerType(null)}>
				<div className="w-full flex flex-col gap-5">
					<div>
						<p className="text-xl font-semibold text-gray-900">We have identified unfixable issues</p>
						<p className="text-sm text-gray-500 mt-1">
							Please review the errors below and re-upload the correct document to get your visas.
						</p>
					</div>

					{formErrorItems?.length > 0 && (
						<>
							<div>
								<p className="text-base font-semibold text-gray-900 mb-3">Errors Found</p>
								<div className="flex flex-wrap gap-2">
									{formErrorItems?.map((item) => (
										<span
											key={item?.validation_name}
											className="flex items-center gap-1.5 text-sm font-medium text-red-600 bg-red-50 px-3 py-1.5 rounded-full border border-red-100">
											<ProhibitInset size={15} weight="fill" />
											{item?.error_text || "Unknown error"}
										</span>
									))}
								</div>
							</div>

							{/* <div>
								<p className="text-base font-semibold text-gray-900 mb-3">Next Steps</p>
								<div className="flex flex-col gap-4">
									{formErrorItems.map((item, index) => (
										<div key={item.validation_name} className="flex gap-3">
											<div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
												{index + 1}
											</div>
											<p className="text-sm font-semibold text-gray-900">{item.error_text}</p>
										</div>
									))}
								</div>
							</div> */}
						</>
					)}

					<div className="flex flex-col gap-3">
						<label
							htmlFor={reUploadInputId}
							className={`flex w-full items-center justify-center gap-2 bg-gradient-to-b from-[#E5483B] to-[#B42318] shadow-[0_0_0_1.2px_#B80000] rounded-xl text-sm font-medium px-3 py-3 text-white ${isDocumentLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
							Re-Upload <UploadSimple size={16} className="text-white" />
						</label>
						{onViewFormErrors && (
							<button
								onClick={() => {
									setDrawerType(null);
									onViewFormErrors();
								}}
								className="w-full text-center font-semibold text-sm text-gray-900 py-2">
								Fix in Form
							</button>
						)}
					</div>
				</div>
			</BottomDrawer>
		</div>
	);
};

export default DocumentListItem;

